import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from '@angular/router';
import { Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { BannerItem, GetBannerParams } from "../../models/banner-interface";
import { BannerApiService } from "../../services/banner-api.service";
import { TranslateService } from "@ngx-translate/core";

export interface ResolvedBannerSlide {
  item: BannerItem;
  desktop: string;
  mobile: string;
}

@Component({
  selector: "app-home-banner",
  templateUrl: "./home-banner.component.html",
  styleUrls: ["./home-banner.component.scss"],
  standalone: false,
})
export class HomeBannerComponent implements OnInit, OnDestroy {
  images: { desktop: string; mobile: string }[] = [];
  bannerItems: BannerItem[] = [];
  resolvedImages: { desktop: string; mobile: string }[] = [];
  primarySlides: ResolvedBannerSlide[] = [];
  sideSlides: ResolvedBannerSlide[] = [];
  index: number = 0;
  loading = false;
  error: string | null = null;

  private sub = new Subscription();

  constructor(
    private bannerApi: BannerApiService,
    private router: Router,
    private translate: TranslateService
  ) {}

  onSlideClick(banner?: BannerItem | null): void {
    if (!banner) return;
    const rawUrl = banner.redirectUrl ? banner.redirectUrl.trim() : "";
    if (!rawUrl) return;

    if (/^https?:\/\//i.test(rawUrl)) {
      window.location.href = rawUrl;
      return;
    }

    const normalizedUrl = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
    this.router.navigateByUrl(normalizedUrl).catch(() => {
      window.location.href = normalizedUrl;
    });
  }

  onBannerClick(index: number): void {
    const banner = this.primarySlides[index]?.item ?? this.bannerItems?.[index];
    this.onSlideClick(banner);
  }

  ngOnInit(): void {
    this.loadBanners();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  loadBanners(): void {
    this.loading = true;
    this.error = null;
    const params: GetBannerParams = { pageIndex: 0, pageSize: 20 };
    const s = this.bannerApi.fetchBanners(params).subscribe({
      next: (res) => {
        if (res && res.success && res.data && res.data.items) {
          const items: BannerItem[] = [...res.data.items].sort(
            (a, b) => (a.displayPriority ?? Number.MAX_SAFE_INTEGER) - (b.displayPriority ?? Number.MAX_SAFE_INTEGER),
          );
          this.bannerItems = items;
          const resolveSlides = (banners: BannerItem[], useThumbnail = true): ResolvedBannerSlide[] => banners
            .map((b) => {
              const getUrl = (url?: string | null) => {
                if (!url) return "";
                return url.startsWith("/")
                  ? `${environment.imageURL.replace(/\/$/, "")}${url}`
                  : url;
              };
              const desktop = getUrl(b.desktopImageUrl || b.imageUrl || b.desktopImage || b.image || b.mobileImageUrl || "");
              const mobile = getUrl(b.mobileImageUrl || b.mobileImage
                || (useThumbnail ? b.thumbnailUrl || b.thumbnail : "") || "") || desktop;

              return { item: b, desktop, mobile };
            })
            .filter((u) => !!u.desktop || !!u.mobile);

          const allSlides = resolveSlides(items);

          this.resolvedImages = allSlides.map(({ desktop, mobile }) => ({ desktop, mobile }));
          this.primarySlides = allSlides.slice();
          this.sideSlides = res.data.rightBanners
            ? resolveSlides(res.data.rightBanners, false)
            : allSlides.slice(1, 3);

          this.images = this.primarySlides.map(({ desktop, mobile }) => ({ desktop, mobile }));
          this.index = 0;
        } else {
          this.images = [];
          this.primarySlides = [];
          this.sideSlides = [];
        }
        this.loading = false;
      },
      error: (err) => {
        console.error(this.translate.instant('home.banner.failed_log'), err);
        this.error = this.translate.instant('home.banner.error_msg');
        this.loading = false;
      },
    });
    this.sub.add(s);
  }

  showSlide(i: number) {
    const len = this.primarySlides.length;
    if (len === 0) return;
    if (i < 0) {
      this.index = len - 1;
    } else if (i >= len) {
      this.index = 0;
    } else {
      this.index = i;
    }
  }

  prevSlide() {
    this.showSlide(this.index - 1);
  }

  nextSlide() {
    this.showSlide(this.index + 1);
  }
}
